<template>
  <AppLayout title="FAQ">
    <div class="min-h-screen bg-gray-50">
      <!-- Header -->
      <div class="bg-white shadow">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div class="md:flex md:items-center md:justify-between">
            <div class="flex-1 min-w-0">
              <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                {{ $t('Hilfe & FAQ') }}
              </h2>
            </div>
          </div>
        </div>
      </div>

      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- Error Message -->
        <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-red-800">
                {{ error }}
              </p>
            </div>
          </div>
        </div>

        <!-- Search -->
        <div class="bg-white rounded-lg shadow mb-6 p-6">
          <div class="max-w-md">
            <label for="search" class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('FAQs durchsuchen') }}
            </label>
            <input
              id="search"
              v-model="searchQuery"
              type="text"
              :placeholder="$t('Häufig gestellte Fragen durchsuchen...')"
              class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sonnenglas-500 focus:border-transparent"
              @input="debouncedSearch"
            />
          </div>
        </div>

        <!-- FAQ Items grouped by category -->
        <template v-if="hasCategories && !searchQuery">
          <div
            v-for="(category, catIndex) in faqCategories"
            :key="catIndex"
            class="mb-8"
          >
            <h3 class="text-xl font-semibold text-gray-800 mb-4 px-1">
              {{ category.name }}
            </h3>
            <div class="space-y-4">
              <div
                v-for="faq in category.faqs"
                :key="faq.id"
                class="bg-white rounded-lg shadow"
              >
                <button
                  class="w-full px-6 py-4 text-left focus:outline-none focus:ring-2 focus:ring-sonnenglas-500 focus:ring-inset"
                  @click="toggleFaq(faq.id)"
                >
                  <div class="flex items-center justify-between">
                    <h3 class="text-lg font-semibold text-gray-900 pr-4">
                      {{ faq.question }}
                    </h3>
                    <div class="flex-shrink-0">
                      <svg
                        class="w-5 h-5 text-gray-500 transition-transform duration-200"
                        :class="{ 'transform rotate-180': openFaqs.includes(faq.id) }"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </button>

                <transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="opacity-0 scale-95"
                  enter-to-class="opacity-100 scale-100"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="opacity-100 scale-100"
                  leave-to-class="opacity-0 scale-95"
                >
                  <div v-if="openFaqs.includes(faq.id)" class="px-6 pb-4">
                    <div class="border-t border-gray-200 pt-4">
                      <div class="prose max-w-none text-gray-700" v-html="faq.answer"></div>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </template>

        <!-- Flat FAQ list (when searching or no categories) -->
        <div v-else class="space-y-4">
          <div
            v-for="faq in filteredFaqs"
            :key="faq.id"
            class="bg-white rounded-lg shadow"
          >
            <button
              class="w-full px-6 py-4 text-left focus:outline-none focus:ring-2 focus:ring-sonnenglas-500 focus:ring-inset"
              @click="toggleFaq(faq.id)"
            >
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-semibold text-gray-900 pr-4">
                  {{ faq.question }}
                </h3>
                <div class="flex-shrink-0">
                  <svg
                    class="w-5 h-5 text-gray-500 transition-transform duration-200"
                    :class="{ 'transform rotate-180': openFaqs.includes(faq.id) }"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </button>

            <transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="opacity-0 scale-95"
              enter-to-class="opacity-100 scale-100"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="opacity-100 scale-100"
              leave-to-class="opacity-0 scale-95"
            >
              <div v-if="openFaqs.includes(faq.id)" class="px-6 pb-4">
                <div class="border-t border-gray-200 pt-4">
                  <div class="prose max-w-none text-gray-700" v-html="faq.answer"></div>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination && pagination.last_page > 1" class="mt-8">
          <nav class="flex items-center justify-between">
            <div class="flex-1 flex justify-between sm:hidden">
              <Link
                v-if="pagination.current_page > 1"
                :href="getPaginationUrl(pagination.current_page - 1)"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {{ $t('Vorherige') }}
              </Link>
              <Link
                v-if="pagination.current_page < pagination.last_page"
                :href="getPaginationUrl(pagination.current_page + 1)"
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {{ $t('Nächste') }}
              </Link>
            </div>
            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p class="text-sm text-gray-700">
                  {{ $t('Zeige') }}
                  <span class="font-medium">{{ pagination.from }}</span>
                  {{ $t('bis') }}
                  <span class="font-medium">{{ pagination.to }}</span>
                  {{ $t('von') }}
                  <span class="font-medium">{{ pagination.total }}</span>
                  {{ $t('Fragen') }}
                </p>
              </div>
              <div>
                <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <Link
                    v-if="pagination.current_page > 1"
                    :href="getPaginationUrl(pagination.current_page - 1)"
                    class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    ‹
                  </Link>
                  <span
                    v-for="page in getPageNumbers()"
                    :key="page"
                    class="relative inline-flex items-center px-4 py-2 border text-sm font-medium"
                    :class="page === pagination.current_page
                      ? 'z-10 bg-sonnenglas-50 border-sonnenglas-500 text-sonnenglas-600'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'"
                  >
                    <Link
                      v-if="page !== pagination.current_page"
                      :href="getPaginationUrl(page)"
                    >
                      {{ page }}
                    </Link>
                    <span v-else>{{ page }}</span>
                  </span>
                  <Link
                    v-if="pagination.current_page < pagination.last_page"
                    :href="getPaginationUrl(pagination.current_page + 1)"
                    class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    ›
                  </Link>
                </nav>
              </div>
            </div>
          </nav>
        </div>

        <!-- Empty State -->
        <div v-if="filteredFaqs.length === 0 && (!hasCategories || searchQuery)" class="text-center py-12">
          <div class="text-gray-500">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('Keine FAQs gefunden') }}</h3>
            <p class="mt-1 text-sm text-gray-500">
              {{ searchQuery
                ? $t('Keine FAQs entsprechen deinen Suchkriterien.')
                : $t('Es gibt keine FAQs zur Anzeige.')
              }}
            </p>
          </div>
        </div>

        <!-- Empty state for categories view -->
        <div v-if="!hasCategories && !searchQuery && faqs.length === 0" class="text-center py-12">
          <div class="text-gray-500">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('No FAQs found') }}</h3>
            <p class="mt-1 text-sm text-gray-500">
              {{ $t('Es gibt keine FAQs zur Anzeige.') }}
            </p>
          </div>
        </div>

        <!-- Contact Section -->
        <div class="mt-12 bg-white rounded-lg shadow p-6">
          <div class="text-center">
            <h3 class="text-lg font-semibold text-gray-900 mb-2">
              {{ $t('Hast du noch Fragen?') }}
            </h3>
            <p class="text-gray-600 mb-4">
              {{ $t('Kontaktiere unser Support-Team für persönliche Unterstützung.') }}
            </p>
            <Link
              :href="route('contactForm')"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-sonnenglas-600 hover:bg-sonnenglas-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
            >
              {{ $t('Support kontaktieren') }}
            </Link>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup>
import { ref, computed, inject } from 'vue'
import { router } from '@inertiajs/vue3'
import { Link } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { debounce } from 'lodash'

const route = inject('route')

const props = defineProps({
  faqs: Array,
  faqCategories: Array,
  pagination: Object,
  filters: Object,
  error: String,
})

const searchQuery = ref(props.filters?.search || '')
const openFaqs = ref([])

const hasCategories = computed(() => {
  return props.faqCategories && props.faqCategories.length > 0
})

const debouncedSearch = debounce(() => {
  router.get(route('faq'), {
    search: searchQuery.value,
  }, {
    preserveState: true,
    preserveScroll: true,
  })
}, 300)

const filteredFaqs = computed(() => {
  if (!searchQuery.value) {
    return props.faqs
  }

  const query = searchQuery.value.toLowerCase()
  return props.faqs.filter(faq =>
    faq.question.toLowerCase().includes(query) ||
    faq.answer.toLowerCase().includes(query)
  )
})

const toggleFaq = (faqId) => {
  const faqIndex = openFaqs.value.indexOf(faqId)
  if (faqIndex > -1) {
    openFaqs.value.splice(faqIndex, 1)
  } else {
    openFaqs.value.push(faqId)
  }
}

const getPaginationUrl = (page) => {
  return route('faq', {
    search: searchQuery.value,
    page: page,
  })
}

const getPageNumbers = () => {
  if (!props.pagination) return []

  const current = props.pagination.current_page
  const last = props.pagination.last_page
  const pages = []

  // Show up to 5 pages around current page
  const start = Math.max(1, current - 2)
  const end = Math.min(last, current + 2)

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
}
</script>
